import React, { useMemo } from 'react';
import { Zap, Clock } from 'lucide-react';
import {
    PLANOS,
    PUBLICOS,
    FIDELIDADE_OPCOES,
    PlanoComercial,
    classificarPublico,
    inferirPlanoPorPercentuais,
    getFatorFidelidade,
    planosSelecionaveis,
} from '../lib/politicaComercial';

/** Plano ativo a partir dos percentuais (escala de exibição) + flag de seleção manual
 *  do "fora da política". Percentuais preenchidos sem plano oficial correspondente
 *  caem em "fora da política". */
export function resolverPlanoAtivo(
    descTarifa?: number,
    descBandeira?: number,
    foraSelecionado?: boolean
): PlanoComercial | null {
    const fora = PLANOS.find(p => p.foraDaPolitica) ?? null;
    if (foraSelecionado) return fora;
    if (!descTarifa && !descBandeira) return null;
    return inferirPlanoPorPercentuais(descTarifa || 0, descBandeira || 0) ?? fora;
}

interface PlanoMultiplicadoresTableProps {
    /** Percentuais em escala de exibição (ex: 22, 50) — destacam o plano correspondente. */
    descTarifa?: number;
    descBandeira?: number;
    /** Volume (kWh) usado para classificar o público e destacar a coluna. */
    volumeKwh?: number;
    fidelidade?: string | null;
    /** Marca a linha "fora da política" como seleção manual do usuário. */
    foraSelecionado?: boolean;
    /** Quando informado, todas as linhas ficam clicáveis (planos oficiais aplicam
     *  os percentuais; "fora da política" habilita os campos manuais no pai). */
    onSelectPlano?: (plano: PlanoComercial) => void;
}

const fmtMult = (v: number) => `${v.toFixed(2).replace('.', ',')}x`;

/** Tabela da política comercial (planos × público) — a mesma referência do modal
 *  Alterar Plano, para o vendedor enxergar a condição enquanto cria a negociação/UC. */
const PlanoMultiplicadoresTable: React.FC<PlanoMultiplicadoresTableProps> = ({
    descTarifa,
    descBandeira,
    volumeKwh,
    fidelidade,
    foraSelecionado,
    onSelectPlano,
}) => {
    const volume = Number(volumeKwh) || 0;
    const publico = useMemo(() => classificarPublico(volume), [volume]);

    const planoAtual = useMemo(
        () => resolverPlanoAtivo(descTarifa, descBandeira, foraSelecionado),
        [descTarifa, descBandeira, foraSelecionado]
    );

    // Plano descontinuado só aparece se for justamente o que está aplicado —
    // UC antiga mostra a condição real do contrato, venda nova não o oferece.
    // "Fora da política" não é linha da tabela: fica atrás do aviso em
    // CondicaoEspecialFields, que o pai renderiza abaixo.
    const linhas = useMemo(
        () => planosSelecionaveis(planoAtual?.codigo).filter(p => !p.foraDaPolitica),
        [planoAtual]
    );

    const fatorFidelidade = getFatorFidelidade(fidelidade, planoAtual?.codigo);
    const fidelidadeLabel = FIDELIDADE_OPCOES.find(o => o.valor.toLowerCase() === (fidelidade || '').toLowerCase())?.label
        ?? (fidelidade || 'Isento de fidelidade');
    const multFinal = planoAtual ? Math.round(planoAtual.mult[publico] * fatorFidelidade * 100) / 100 : null;

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase">
                    Planos da política comercial
                </label>
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                        publico === 'A' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400'
                        : publico === 'B' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400'
                    }`}>
                        <Zap size={11} />
                        {PUBLICOS[publico].label}
                        <span className="font-normal opacity-80">· {PUBLICOS[publico].faixaLabel}</span>
                    </span>
                    <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400"
                        title="Fidelidade definida na negociação — multiplica o fator do plano"
                    >
                        <Clock size={11} />
                        {fidelidadeLabel}
                        <span className="font-normal opacity-80">· {fmtMult(fatorFidelidade)}</span>
                    </span>
                </div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-slate-800/60 text-gray-500 dark:text-slate-400">
                                <th className="text-left px-3 py-2 font-bold uppercase text-[10px]">Plano</th>
                                {(['C', 'B', 'A'] as const).map(p => (
                                    <th key={p} className={`px-2 py-2 font-bold uppercase text-[10px] text-center ${p === publico ? 'bg-primary/10 text-primary' : ''}`}>
                                        Púb. {p}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                            {linhas.map(plano => {
                                const isRow = planoAtual?.codigo === plano.codigo;
                                // Plano descontinuado nunca é selecionável: ele só está
                                // na tabela para mostrar a condição já fechada.
                                const clicavel = !!onSelectPlano && !plano.legado;
                                return (
                                    <tr
                                        key={plano.codigo}
                                        onClick={clicavel ? () => onSelectPlano!(plano) : undefined}
                                        className={`${
                                            plano.legado
                                                ? 'border-t-2 border-dashed border-red-200 dark:border-red-900/50 bg-red-50/40 dark:bg-red-900/10'
                                                : isRow ? 'bg-primary/5' : ''
                                        } ${clicavel ? 'cursor-pointer hover:bg-primary/10 transition-colors' : ''}`}
                                        title={plano.legado
                                            ? 'Plano descontinuado — mantido apenas para mostrar a condição já fechada'
                                            : clicavel
                                                ? (plano.foraDaPolitica
                                                    ? 'Clique para informar percentuais manuais (exige aprovação)'
                                                    : 'Clique para aplicar os percentuais deste plano')
                                                : undefined}
                                    >
                                        <td className={`px-3 py-2 ${
                                            plano.legado ? 'font-bold text-red-700 dark:text-red-400'
                                                : isRow ? 'font-bold text-primary'
                                                    : plano.foraDaPolitica ? 'text-orange-600 dark:text-orange-400'
                                                        : 'text-gray-600 dark:text-slate-300'
                                        }`}>
                                            {plano.label}
                                            {!plano.foraDaPolitica && (
                                                <span className={`block text-[10px] font-normal ${plano.legado ? 'text-red-500/80' : 'text-gray-400 dark:text-slate-500'}`}>
                                                    {plano.descTarifa}% tarifa · {plano.descBandeira}% bandeira
                                                </span>
                                            )}
                                            {plano.foraDaPolitica && (
                                                <span className="block text-[10px] font-normal text-orange-500/80">Exige aprovação do gerente</span>
                                            )}
                                            {plano.legado && (
                                                <span className="block text-[10px] font-bold text-red-600 dark:text-red-400 mt-0.5">
                                                    Plano descontinuado — não vale para vendas novas
                                                </span>
                                            )}
                                        </td>
                                        {(['C', 'B', 'A'] as const).map(p => (
                                            <td key={p} className={`px-2 py-2 text-center font-mono ${
                                                plano.legado
                                                    ? p === publico
                                                        ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 font-bold'
                                                        : 'text-red-500/80 dark:text-red-400/70'
                                                    : p === publico && isRow
                                                        ? 'bg-primary text-white font-bold rounded'
                                                        : p === publico
                                                            ? 'bg-primary/10 text-primary font-semibold'
                                                            : isRow
                                                                ? 'font-semibold text-gray-700 dark:text-slate-200'
                                                                : 'text-gray-500 dark:text-slate-400'
                                            }`}>
                                                {fmtMult(plano.mult[p])}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="px-3 py-2 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/40 flex flex-wrap items-center justify-between gap-2">
                    {planoAtual ? (
                        <p className="text-[11px] text-gray-600 dark:text-slate-300">
                            <span className={`font-bold ${
                                planoAtual.legado ? 'text-red-700 dark:text-red-400'
                                    : planoAtual.foraDaPolitica ? 'text-orange-600 dark:text-orange-400'
                                        : 'text-primary'
                            }`}>{planoAtual.label}</span>
                            <span className="text-gray-400 dark:text-slate-500"> · multiplicador </span>
                            <span className="font-mono font-bold">{fmtMult(multFinal!)}</span>
                            <span className="text-gray-400 dark:text-slate-500">
                                {' '}({fmtMult(planoAtual.mult[publico])} plano × {fmtMult(fatorFidelidade)} fidelidade)
                            </span>
                        </p>
                    ) : (
                        <p className="text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                            Selecione um plano na tabela para definir os descontos.
                        </p>
                    )}
                    {onSelectPlano && (
                        <p className="text-[10px] text-gray-400 dark:text-slate-500">Clique em um plano para aplicar os percentuais.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PlanoMultiplicadoresTable;
